/** Required extension for the index workbook. */
export const INDEX_FILE_EXTENSION: string = '.xlsx';

/** Keep in sync with `_validators` in the API's `FileValidationService.cs` (SurfaceSE.ESRA.Common/Helpers/File/ValidationServices). */
export const ALLOWED_DOCUMENT_EXTENSIONS: ReadonlyArray<string> = ['.xlsx', '.pdf', '.docx', '.pptx', '.doc', '.xls', '.ppt', '.zip'];

export const ALLOWED_DOCUMENT_EXTENSION_SET: Set<string> = new Set<string>(ALLOWED_DOCUMENT_EXTENSIONS);

/** Keep in sync with `MaxAllowedFileSizeInBytes` in the API's `FileValidationService.cs`; applies to every file. */
export const MAX_FILE_SIZE_MB: number = 50;

export const MAX_FILE_SIZE_BYTES: number = MAX_FILE_SIZE_MB * 1024 * 1024;

/** Most documents sent in one document request. Keeps a request small enough to finish quickly and to be retried on its own. */
export const DOCUMENT_UPLOAD_BATCH_SIZE: number = 5;

/** Soft limit of the bytes in one document request; a single file larger than this is still sent alone. */
export const DOCUMENT_UPLOAD_BATCH_MAX_BYTES: number = 25 * 1024 * 1024;

/** Most document requests in flight at the same time. */
export const DOCUMENT_UPLOAD_MAX_CONCURRENT_REQUESTS: number = 3;

/**
 * Splits `files` into document requests: at most `maxFiles` files and about `maxBytes` bytes each, in the given order.
 * A file larger than `maxBytes` still gets a batch of its own, so nothing is dropped.
 * @param files The files to send.
 * @param maxFiles Most files in one batch.
 * @param maxBytes Soft limit of the bytes in one batch.
 */
export function createUploadBatches(
  files: ReadonlyArray<File>,
  maxFiles: number = DOCUMENT_UPLOAD_BATCH_SIZE,
  maxBytes: number = DOCUMENT_UPLOAD_BATCH_MAX_BYTES,
): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes: number = 0;

  for (const file of files) {
    const wouldOverflow: boolean = current.length > 0 && (current.length >= maxFiles || currentBytes + file.size > maxBytes);
    if (wouldOverflow) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }

  if (current.length > 0) {
    batches.push(current);
  }
  return batches;
}

/** Returns the lower-cased extension of `fileName` including the dot, or an empty string when there is none. */
export function getFileExtension(fileName: string): string {
  const dotIndex: number = fileName.lastIndexOf('.');
  return dotIndex < 0 ? '' : fileName.substring(dotIndex).toLowerCase();
}

/**
 * Splits `files` into the ones the API accepts and the names it would reject (unsupported type or over the size limit).
 * @param files The files the user picked or dropped.
 */
export function filterAcceptedDocuments(files: ReadonlyArray<File>): { accepted: File[]; unsupportedNames: string[]; oversizedNames: string[] } {
  const accepted: File[] = [];
  const unsupportedNames: string[] = [];
  const oversizedNames: string[] = [];

  for (const file of files) {
    if (!ALLOWED_DOCUMENT_EXTENSION_SET.has(getFileExtension(file.name))) {
      unsupportedNames.push(file.name);
    } else if (file.size > MAX_FILE_SIZE_BYTES) {
      oversizedNames.push(file.name);
    } else {
      accepted.push(file);
    }
  }

  return { accepted, unsupportedNames, oversizedNames };
}

/** Key of the index column that holds a row's file name; keep in sync with `DocumentIndexSchema.FileNameKey` in the API. */
export const FILE_NAME_COLUMN_KEY: string = 'Filename';

/**
 * Compares file names the way the API does: case-insensitive, ignoring surrounding spaces.
 * @param fileName A file name or an index cell value.
 */
export function normalizeFileName(fileName: string | null | undefined): string {
  return (fileName ?? '').trim().toLowerCase();
}

/**
 * Splits `files` into the ones whose name is unique in the selection and the names that more than one file carries.
 * Which of several files with the same name the user meant is unknowable, so none of them is used.
 * @param files The files of one selection or one upload batch.
 */
export function splitAmbiguousFileNames(files: ReadonlyArray<File>): { unique: File[]; ambiguousNames: string[] } {
  const countsByName: Map<string, number> = new Map<string, number>();
  for (const file of files) {
    const key: string = normalizeFileName(file.name);
    countsByName.set(key, (countsByName.get(key) ?? 0) + 1);
  }

  const ambiguousKeys: Set<string> = new Set<string>();
  const ambiguousNames: string[] = [];
  const unique: File[] = [];
  for (const file of files) {
    const key: string = normalizeFileName(file.name);
    if ((countsByName.get(key) ?? 0) > 1) {
      if (!ambiguousKeys.has(key)) {
        ambiguousKeys.add(key);
        ambiguousNames.push(file.name);
      }
    } else {
      unique.push(file);
    }
  }

  return { unique, ambiguousNames };
}

/** The message shown when more than one selected file has the same name. */
export function describeAmbiguousFileName(fileName: string): string {
  return `More than one selected file has the name '${fileName}'. Keep one file and try again.`;
}